<template>
  <div class="input-container" v-if="!activeFileSrc">
    <div class="label-title label-format-title">
      {{ formatTitle }}
    </div>
    <v-file-input
      :model-value="activeFile"
      :hide-details="true"
      :prepend-icon="false"
      :clearable="false"
      :accept="file.mimetype"
      @update:model-value="(value) => uploadFile(value)"
      label="File input"
    >
      <template v-slot:label>
        {{ file.placeholder }}
      </template>
      <template v-slot:append-inner>
        <icon-default :color="'dark-green'">
          <FileUploadIcon />
        </icon-default>
      </template>
    </v-file-input>
  </div>
  <div class="input-file-container" v-else-if="activeFileSrc && !isActiveImg">
    <div class="left-container">
      <icon-default :color="'dark-green'" :size="'mini'">
        <CopyIcon />
      </icon-default>
      <div class="center-container">
        <div class="title-container">
          <main-title>
            {{ fileComputed.name }}
          </main-title>
          <main-title class="size-title">
            {{ fileComputed.size }}
          </main-title>
        </div>
        <line-timer v-if="fileComputed.loading" :time="30" />
        <main-title class="red-title" v-else-if="fileComputed.error">
          {{ fileComputed.error }}
        </main-title>
      </div>
    </div>
    <div class="right-container">
      <icon-default
        v-if="fileComputed.error"
        @click="$emit('reloadFile', file)"
        :color="'dark-green'"
        :size="'mini'"
      >
        <ReloadIcon />
      </icon-default>
      <icon-default
        class="cross"
        @click="$emit('removeFile', file)"
        :color="'dark-green'"
        :size="'mini'"
      >
        <CrossIcon />
      </icon-default>
    </div>
  </div>
  <div
    class="input-file-img-container"
    v-else-if="activeFileSrc && isActiveImg"
  >
    <icon-default
      size="mini"
      class="cross"
      @click="$emit('removeFile', file)"
      @mouseleave="focusFalse"
      @mouseenter="focusTrue"
      :color="'dark-green'"
    >
      <IconCross />
    </icon-default>
    <img
      v-if="!isVideo"
      :src="getImg(activeFile)"
      alt="file"
      @error="handlePreviewError"
    />
    <video v-else :src="activeFile.fileSrc" alt="file" />
  </div>
</template>

<script>
import IconDefault from "@/components/shared/icons/IconDefault.vue";
import FileUploadIcon from "@/assets/icons/ui/upload.svg?inline";
import CopyIcon from "@/assets/icons/ui/copy.svg?inline";
import CrossIcon from "@/assets/icons/ui/cross.svg?inline";
import ReloadIcon from "@/assets/icons/ui/reload.svg?inline";
import IconCross from "@/assets/icons/ui/cross.svg?inline";
import MainTitle from "@/components/shared/text/MainTitle.vue";
import LineTimer from "@/components/shared/LineTimer.vue";

export default {
  name: "FileInput",
  components: {
    LineTimer,
    MainTitle,
    IconDefault,
    IconCross,
    FileUploadIcon,
    CopyIcon,
    ReloadIcon,
    CrossIcon,
  },
  props: {
    isActiveImg: {
      type: Boolean,
      default: false,
    },
    file: {
      type: Object,
      required: true,
    },
    activeFile: {},
    isVideo: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      focusParam: false,
      verificationTitle: "Файл прикреплен",
    };
  },
  computed: {
    activeFileSrc() {
      return this.activeFile?.file || this.activeFile?.logoKey;
    },
    formatTitle() {
      return `${this.file.title} — ${this.file.mimeTypeString}`;
    },
    nameFile() {
      return this.file?.key || this.activeFile?.s3Key;
    },
    fileComputed() {
      let file = this.activeFile?.file;
      return {
        name: file?.originalName || file?.name || "-",
        loading: false,
        error: false,
        size: this.convertSize(file.size || 0),
      };
    },
  },
  methods: {
    getImg(file) {
      if (file.logoKey) {
        return this.getS3Key(file.logoKey);
      } else {
        return file.fileSrc;
      }
    },
    convertSize(bytes) {
      if (bytes < 1024) {
        return `${bytes} Б`;
      } else if (bytes < 1024 ** 2) {
        return `${(bytes / 1024).toFixed(2)} КБ`;
      } else if (bytes < 1024 ** 3) {
        return `${(bytes / 1024 ** 2).toFixed(2)} МБ`;
      } else {
        return `${(bytes / 1024 ** 3).toFixed(2)} ГБ`;
      }
    },
    async uploadFile(file) {
      if (file) {
        if (!this.file.mimetype.includes(file.type)) {
          this.alertError("Не тот тип файла!");
          return;
        }
        const maxSize = this.file.size
          ? this.file.size * 1_000_000_000
          : 10_000_000_000;
        if (file.size > maxSize) {
          this.alertError("Размер файла слишком большой!");
          return;
        }
        try {
          const fileSrc = await this.readFileAsDataUrl(file);
          if (this.isActiveImg) {
            if (this.isVideo) {
              this.$emit("changeFile", this.file, file, fileSrc);
            } else {
              this.openModalCropImg(file, fileSrc);
            }
          } else {
            this.$emit("changeFile", this.file, file, fileSrc);
          }
        } catch {
          this.alertError("Не удалось открыть файл для предпросмотра");
        }
      }
    },
    readFileAsDataUrl(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error);
        reader.onabort = () => reject(new Error("Чтение файла прервано"));
        reader.readAsDataURL(file);
      });
    },
    handlePreviewError() {
      if (!this.activeFile?.file) return;
      this.alertError(
        "Предпросмотр изображения не загрузился. Выберите файл повторно."
      );
      this.$emit("removeFile", this.file);
    },
    cropImg(file, fileSrc) {
      this.$emit("changeFile", this.file, file, fileSrc);
    },
  },
};
</script>

<style scoped lang="scss">
.input-file-img-container {
  border-radius: 8px;
  overflow: hidden;
  width: 260px;
  height: 180px;
  position: relative;

  .icon-container {
    z-index: 1;
    cursor: pointer;
    position: absolute;
    right: 8px;
    top: 8px;
  }

  & video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
  & img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}
.input-file-container {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  background: rgba(var(--color-grey-rgba), 1);
  border-radius: 8px;
  padding: 10px 16px;

  .left-container {
    display: flex;
    flex-direction: row;
    column-gap: 16px;
    align-items: center;
    .center-container {
      display: flex;
      flex-direction: column;
      row-gap: 2px;
      width: 300px;

      .title-container {
        display: flex;
        flex-direction: row;
        align-items: center;
        column-gap: 8px;

        .main-title-container {
          color: var(--color-dark-green);
          &.size-title {
            color: rgba(var(--color-ligth-green), 1);
          }
        }
      }
    }
  }
  .right-container {
    display: flex;
    flex-direction: row;
    column-gap: 8px;

    .icon-container {
      cursor: pointer;
    }
  }
}

.input-container {
  width: 100%;
  position: relative;
  height: 82px;
  cursor: pointer;

  @media (max-width: 769px) {
    height: 100px;
  }

  .label-format-title {
    padding: 0 16px;
    pointer-events: none;
    position: absolute;
    font-weight: 100;
    z-index: 900;
    top: 70%;
    transform: translate(-50%, -70%);
    left: 50%;
    font-size: 12px;
    color: rgba(var(--color-ligth-green), 1);
    text-align: center;
    width: 100%;
  }
}
.v-file-input {
  display: block;
  cursor: pointer !important;
  width: 100%;
  height: 100%;
  background: rgba(var(--color-grey-rgba), 0.4);
  border-radius: 8px;
  opacity: 1;
  border: 2px dashed rgba(var(--color-dark-green-rgb), 0.6);
  &:hover {
    background: rgba(var(--color-grey-rgba), 0.6);
    border: 2px dashed rgba(var(--color-dark-green-rgb), 0.8);
    :deep() {
      .v-label.v-field-label {
        color: var(--color-dark-green);
      }
    }
  }
  :deep() {
    font-weight: 100;
    color: var(--color-dark-green);
    .v-label.v-field-label {
      cursor: pointer;
      position: relative;
      visibility: visible !important;
      font-size: 14px !important;
      top: 30%;
      opacity: 1 !important;
      transform: scale(1) translateY(-30%) !important;
      width: fit-content !important;
      min-width: max-content;
      transition: color 0.3s ease;
      @media (max-width: 769px) {
        font-size: 12px !important;
      }
    }
    .v-field__input,
    .v-field__overlay,
    .v-field__loader {
      display: none;
    }
    .v-field__field {
      flex: none;
    }
    input {
      cursor: pointer;
    }
    .v-field__append-inner {
      height: 24px;
      position: relative;
      top: 30%;
      transform: translateY(-30%) !important;
    }
    .v-field {
      opacity: 1;
      display: flex;
      width: fit-content;
      justify-content: center;
      border: none;
    }
    .v-input__control {
      cursor: pointer;
      height: 100%;
    }
    .v-field__outline {
      display: none;
    }
    .v-field__field {
      width: fit-content;
    }
    .v-field--center-affix .v-label.v-field-label--floating {
      display: none;
    }
  }
}
</style>
